import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Artikel, Vertrag } from '../types';
import artikelService from '../services/artikelService';
import vertragService from '../services/vertragService';
import rechnungService from '../services/rechnungService';
import KundeSelect from './KundeSelect';
import { Modal, Field, Input, DateInput, Select, Button, FormActions, Icon, inputClass } from './ui';
import { formatPreis, zahlZuText, textZuZahl } from '../utils/preis';

interface Zeile {
  key: number;
  bezeichnung: string;
  mengeText: string;
  preisText: string;
  mwstText: string;
}

let naechsterKey = 1;

const heuteIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Schnellrechnung: eine Rechnung von Hand zwischendurch, z. B. wenn ein Kunde anruft und kurz sein
 * Grab gepflegt haben möchte. Zeilen kommen aus der Preisliste (Bezeichnung, Preis, MwSt vorbelegt
 * und änderbar) oder frei. Hängt nicht an einer Vertragsposition und läuft deshalb nicht in die
 * Jahresabrechnung. Die Grabstelle ist optional und steht dann auf der Rechnung.
 *
 * Beträge rechnet ausschließlich das Backend – hier wird nichts summiert.
 */
const SchnellrechnungModal: React.FC<{
  /** Fester Kunde (Kunden-Detailseite); ohne Angabe wird er im Dialog gewählt. */
  kundeId?: number;
  onClose: () => void;
}> = ({ kundeId: festerKunde, onClose }) => {
  const navigate = useNavigate();
  const [kundeId, setKundeId] = useState<number | undefined>(festerKunde);
  const [vertraege, setVertraege] = useState<Vertrag[]>([]);
  const [vertragId, setVertragId] = useState('');
  const [leistungsdatum, setLeistungsdatum] = useState(heuteIso());
  const [artikel, setArtikel] = useState<Artikel[]>([]);
  const [suche, setSuche] = useState('');
  const [zeilen, setZeilen] = useState<Zeile[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    artikelService.getAll().then(setArtikel).catch(() => setArtikel([]));
  }, []);

  // Grabstellen (über die Verträge) des gewählten Kunden – nur laufende sind sinnvoll.
  useEffect(() => {
    setVertragId('');
    if (!kundeId) { setVertraege([]); return; }
    vertragService.getByKunde(kundeId)
      .then(vs => {
        const laufend = vs.filter(v => v.active && v.status !== 'BEENDET');
        setVertraege(laufend);
        if (laufend.length === 1) setVertragId(String(laufend[0].id));
      })
      .catch(() => setVertraege([]));
  }, [kundeId]);

  const treffer = useMemo(() => {
    const q = suche.trim().toLowerCase();
    if (!q) return [];
    return artikel
      .filter(a => a.active && `${a.artikelnummer} ${a.bezeichnung}`.toLowerCase().includes(q))
      .slice(0, 20);
  }, [artikel, suche]);

  const neueZeile = (a?: Artikel) => {
    setZeilen(zs => [...zs, {
      key: naechsterKey++,
      bezeichnung: a?.bezeichnung ?? '',
      mengeText: '1',
      preisText: a ? zahlZuText(a.preis ?? 0) : '',
      mwstText: zahlZuText(a?.mwstSatz ?? 19),
    }]);
    setSuche('');
  };

  const aendere = (key: number, teil: Partial<Zeile>) =>
    setZeilen(zs => zs.map(z => (z.key === key ? { ...z, ...teil } : z)));

  const absenden = async (stellen: boolean) => {
    if (!kundeId) { toast.error('Bitte einen Kunden wählen'); return; }
    if (zeilen.length === 0) { toast.error('Bitte mindestens eine Position hinzufügen'); return; }
    const positionen = [];
    for (const z of zeilen) {
      if (!z.bezeichnung.trim()) { toast.error('Jede Position braucht eine Bezeichnung'); return; }
      const menge = textZuZahl(z.mengeText);
      const einzelpreis = textZuZahl(z.preisText);
      const mwstSatz = textZuZahl(z.mwstText);
      if (menge == null || menge <= 0) { toast.error(`„${z.bezeichnung}“: Menge muss größer als 0 sein`); return; }
      if (einzelpreis == null) { toast.error(`„${z.bezeichnung}“: Preis ist keine gültige Zahl`); return; }
      if (mwstSatz == null || mwstSatz > 100) { toast.error(`„${z.bezeichnung}“: MwSt-Satz muss zwischen 0 und 100 liegen`); return; }
      positionen.push({ bezeichnung: z.bezeichnung.trim(), menge, einzelpreis, mwstSatz, leistungVon: leistungsdatum || null });
    }
    if (stellen && !window.confirm('Rechnung stellen? Sie bekommt eine Rechnungsnummer und ist danach unveränderlich.')) return;

    setSaving(true);
    try {
      const r = await rechnungService.create({
        kundeId, vertragId: vertragId ? parseInt(vertragId, 10) : null, positionen, stellen,
      });
      toast.success(stellen ? `Rechnung ${r.rechnungsnummer} gestellt` : 'Rechnungsentwurf angelegt');
      navigate(`/rechnungen/${r.id}`);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Rechnung konnte nicht angelegt werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Schnellrechnung" onClose={onClose}>
      <div className="space-y-4">
        {!festerKunde && (
          <Field label="Kunde" required>
            <KundeSelect value={kundeId} onChange={setKundeId} allowClear={false} autoFocus />
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Grabstelle" hint="Optional – steht dann auf der Rechnung.">
            <Select value={vertragId} onChange={e => setVertragId(e.target.value)} disabled={vertraege.length === 0}>
              <option value="">— keine —</option>
              {vertraege.map(v => (
                <option key={v.id} value={v.id}>
                  {v.grabNummer ?? 'Grab'}{v.tierName ? ` · ${v.tierName}` : ''} ({v.vertragsnummer})
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Leistungsdatum" required>
            <DateInput value={leistungsdatum} onChange={setLeistungsdatum} />
          </Field>
        </div>

        <div>
          <p className="mb-1 text-sm font-medium text-gray-700">Positionen</p>
          {zeilen.length === 0 && (
            <p className="text-sm text-gray-500">Noch keine Positionen – Artikel unten suchen oder eine freie Position anlegen.</p>
          )}
          <div className="space-y-2">
            {zeilen.map(z => (
              <div key={z.key} className="rounded-lg border border-gray-200 p-3">
                <div className="mb-2 flex items-center gap-2">
                  <Input className="flex-1" placeholder="Bezeichnung" value={z.bezeichnung} onChange={e => aendere(z.key, { bezeichnung: e.target.value })} />
                  <button
                    type="button"
                    onClick={() => setZeilen(zs => zs.filter(x => x.key !== z.key))}
                    aria-label="Position entfernen"
                    className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <Icon.Trash className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <label className="text-xs text-gray-500">Menge
                    <Input inputMode="decimal" value={z.mengeText} onChange={e => aendere(z.key, { mengeText: e.target.value })} />
                  </label>
                  <label className="text-xs text-gray-500">Einzelpreis netto
                    <Input inputMode="decimal" value={z.preisText} onChange={e => aendere(z.key, { preisText: e.target.value })} />
                  </label>
                  <label className="text-xs text-gray-500">MwSt %
                    <Input inputMode="decimal" value={z.mwstText} onChange={e => aendere(z.key, { mwstText: e.target.value })} />
                  </label>
                </div>
              </div>
            ))}
          </div>

          <input
            placeholder="Artikel aus der Preisliste suchen…"
            value={suche}
            onChange={e => setSuche(e.target.value)}
            className={`${inputClass} mt-2`}
          />
          {treffer.length > 0 && (
            <ul className="mt-2 max-h-48 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
              {treffer.map(a => (
                <li key={a.id}>
                  <button type="button" onClick={() => neueZeile(a)} className="flex w-full items-baseline gap-2 px-3 py-2 text-left hover:bg-brand-50">
                    <span className="whitespace-nowrap text-xs tabular-nums text-gray-400">{a.artikelnummer}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{a.bezeichnung}</span>
                    <span className="whitespace-nowrap text-xs tabular-nums text-gray-500">{formatPreis(a.preis)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button type="button" onClick={() => neueZeile()} className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline">
            <Icon.Plus className="h-4 w-4" /> Freie Position
          </button>
        </div>

        <FormActions>
          <Button type="button" variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button type="button" variant="secondary" loading={saving} onClick={() => absenden(false)}>Als Entwurf anlegen</Button>
          <Button type="button" loading={saving} onClick={() => absenden(true)}>Anlegen &amp; stellen</Button>
        </FormActions>
      </div>
    </Modal>
  );
};

export default SchnellrechnungModal;
